import { Inject, Injectable } from '@angular/core';
import { BaseHttpService } from '../../../modules/fwcore';
import { HttpClient } from '@angular/common/http';
import { OKTA_AUTH } from '@okta/okta-angular';
import OktaAuth from '@okta/okta-auth-js';
import { Observable, of } from 'rxjs';
import { map } from 'rxjs/operators';
import urlJoin from 'url-join';
import { FirstUpFeedResponse, FirstUpPost, VoicestormLatest, VoicestormPosts } from '../models';

@Injectable({
  providedIn: 'root'
})
export class FirstUpService extends BaseHttpService {
  private firstUpSuffix: string = 'firstup';

  constructor(
    protected http: HttpClient,
    @Inject(OKTA_AUTH) private oauth: OktaAuth,
    @Inject('CONFIG_SETTINGS') configSettings: any
  ) {
    super(http, oauth);

    const genSettings = configSettings['general'];
    this.baseUrl = genSettings['adminApiUrl'];
    this.apiKey = genSettings['apiKey'];
  }

  /**
   * Gets feed posts from FirstUp API via backend
   * @param limit Maximum number of posts to return
   * @param offset Number of posts to skip for pagination
   * @returns Observable of FirstUpFeedResponse
   */
  public getFeed(limit: number = 10, offset: number = 0): Observable<FirstUpFeedResponse> {
    const token = this.getToken();

    if (token != null) {
      return this.getItem<FirstUpFeedResponse>(
        urlJoin(this.baseUrl, this.firstUpSuffix, `feed?limit=${limit}&offset=${offset}`),
        this.getHttpBearerHeaders(token)
      );
    }
    else {
      return of({ data: [], meta: { totalCount: 0 } });
    }
  }

  /**
   * Gets feed posts and maps them to VoicestormLatest format for UI compatibility
   * This allows existing UI components to work without changes
   * @param limit Maximum number of posts to return
   * @returns Observable of VoicestormPosts<VoicestormLatest>
   */
  public getLatestNews(limit: number = 10): Observable<VoicestormPosts<VoicestormLatest>> {
    return this.getFeed(limit, 0).pipe(
      map((response: FirstUpFeedResponse) => this.mapToVoicestormPosts(response))
    );
  }

  /**
   * Maps FirstUp feed response to VoicestormPosts format
   */
  private mapToVoicestormPosts(response: FirstUpFeedResponse): VoicestormPosts<VoicestormLatest> {
    const posts = response.data?.map((post: FirstUpPost) => this.mapFirstUpToVoicestorm(post)) || [];
    return {
      posts: posts,
      totalCount: response.meta?.totalCount || posts.length
    };
  }

  /**
   * Maps a single FirstUp post to VoicestormLatest format
   */
  private mapFirstUpToVoicestorm(post: FirstUpPost): VoicestormLatest {
    // Get first image URL if available
    const imageUrl = post.images && post.images.length > 0 ? post.images[0].imageUrl : '';

    return {
      postId: String(post.id || ''),
      title: post.title || '',
      author: post.author?.displayName || '',
      boxImage: imageUrl || '',
      squareImage: imageUrl || '',
      link: post.externalContentUrl || post.permalinkUrl || '',
      publishDate: post.publishedAt ? new Date(post.publishedAt) : new Date(),
      content: post.summary || post.body || ''
    };
  }
}
